import { supabase } from '../lib/supabase';

const getSupabaseUrl = () => import.meta.env?.VITE_SUPABASE_URL || '';
const getAnonKey = () => import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

const withTimeout = (promise, ms = 8000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
  ]);
};

export const checkSupabaseStatus = async () => {
  if (!supabase || !getSupabaseUrl()) {
    return { status: 'error', message: 'Supabase not configured', latency: null };
  }
  const start = Date.now();
  try {
    await withTimeout(
      supabase?.from('user_profiles')?.select('id')?.limit(1),
      5000
    );
    return { status: 'healthy', message: 'Connected', latency: Date.now() - start };
  } catch (err) {
    return { status: 'error', message: err?.message || 'Connection failed', latency: null };
  }
};

export const checkOpenAIStatus = async () => {
  const key = import.meta.env?.VITE_OPENAI_API_KEY;
  if (!key || key?.includes('your-') || key?.length < 20) {
    return { status: 'error', message: 'OpenAI API key not configured' };
  }
  return { status: 'healthy', message: 'API key configured' };
};

export const checkAnthropicStatus = async () => {
  const key = import.meta.env?.VITE_ANTHROPIC_API_KEY;
  if (!key || key?.includes('your-') || key?.length < 20) {
    return { status: 'error', message: 'Anthropic API key not configured' };
  }
  return { status: 'healthy', message: 'API key configured' };
};

export const checkGeminiStatus = async () => {
  const key = import.meta.env?.VITE_GEMINI_API_KEY;
  if (!key || key?.includes('your-') || key?.length < 20) {
    return { status: 'error', message: 'Gemini API key not configured' };
  }
  return { status: 'healthy', message: 'API key configured' };
};

export const checkStripeStatus = async () => {
  const key = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!key || key?.includes('your-') || key?.length < 20) {
    return { status: 'error', message: 'Stripe publishable key not configured' };
  }
  return { status: 'healthy', message: 'Publishable key configured' };
};

export const checkEdgeFunctionStatus = async (functionName) => {
  const supabaseUrl = getSupabaseUrl();
  if (!supabaseUrl) return { status: 'error', message: 'Supabase URL not configured' };
  const start = Date.now();
  try {
    let token = getAnonKey();
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.access_token) token = session?.access_token;
    } catch (e) {}
    const response = await withTimeout(
      fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': getAnonKey()
        },
        body: JSON.stringify({ _health_check: true })
      }),
      6000
    );
    const latency = Date.now() - start;
    // 200, 400, 422 all mean the function is reachable
    if (response?.status < 500) {
      return { status: 'healthy', message: `Reachable (${response?.status})`, latency };
    }
    return { status: 'warning', message: `Server error (${response?.status})`, latency };
  } catch (err) {
    if (err?.message === 'Timeout') {
      return { status: 'warning', message: 'Slow response (timeout)', latency: null };
    }
    return { status: 'error', message: err?.message || 'Unreachable', latency: null };
  }
};

export const runFullDiagnostics = async () => {
  const [supabaseStatus, openaiStatus, anthropicStatus, geminiStatus, stripeStatus] = await Promise.allSettled([
    checkSupabaseStatus(),
    checkOpenAIStatus(),
    checkAnthropicStatus(),
    checkGeminiStatus(),
    checkStripeStatus(),
  ]);

  const edgeFunctions = ['send-email', 'send-sms', 'make-call', 'create-payment-intent', 'confirm-payment'];
  const edgeResults = await Promise.allSettled(
    edgeFunctions?.map(fn => checkEdgeFunctionStatus(fn))
  );

  const edgeFunctionStatuses = {};
  edgeFunctions?.forEach((fn, i) => {
    edgeFunctionStatuses[fn] = edgeResults?.[i]?.status === 'fulfilled'
      ? edgeResults?.[i]?.value
      : { status: 'error', message: 'Check failed' };
  });

  return {
    timestamp: new Date()?.toISOString(),
    services: {
      supabase: supabaseStatus?.status === 'fulfilled' ? supabaseStatus?.value : { status: 'error', message: 'Check failed' },
      openai: openaiStatus?.status === 'fulfilled' ? openaiStatus?.value : { status: 'error', message: 'Check failed' },
      anthropic: anthropicStatus?.status === 'fulfilled' ? anthropicStatus?.value : { status: 'error', message: 'Check failed' },
      gemini: geminiStatus?.status === 'fulfilled' ? geminiStatus?.value : { status: 'error', message: 'Check failed' },
      stripe: stripeStatus?.status === 'fulfilled' ? stripeStatus?.value : { status: 'error', message: 'Check failed' },
    },
    edgeFunctions: edgeFunctionStatuses,
  };
};

export const diagnosticsService = {
  checkSupabaseStatus,
  checkOpenAIStatus,
  checkAnthropicStatus,
  checkGeminiStatus,
  checkStripeStatus,
  checkEdgeFunctionStatus,
  runFullDiagnostics,
};

export default diagnosticsService;